<template>
  <BaseGraph3d v-bind="$attrs"
               v-on="$listeners"
               :containerStyle="containerStyle"
               :disableAutoCameraOrbit="disableAutoCameraOrbit"
               :disablePopover="disablePopover"
               :extraIndex="extraIndex"
               :graphStyle="graphStyle"
               :height="height"
               :isHighlighted="isHighlighted"
               :is3d="true"
               :labelMaxLength="labelMaxLength"
               :links="links"
               :nodes="nodes"
               :popover="popover"
               :triggerEdgeUpdates="triggerEdgeUpdates"
               :triggerFitView="triggerFitView"
               :triggerNodeUpdates="triggerNodeUpdates"
               :width="width" />
</template>

<script>
  import BaseGraph3d from '@base/base-graph-3d'
  import { FontStyle, Layout } from '@js/styles'
  import { mapGetters } from 'vuex'

  export default {
    name: 'SkillGraph3dPrimary',
    inheritAttrs: false,
    components: {
      BaseGraph3d,
    },
    props: {
      disableAutoCameraOrbit: { type: Boolean, required: false, default: false },
      disableConditionalPopover: { type: Boolean, required: false, default: false },
      disablePopover: { type: Boolean, required: false, default: false },
      extraIndex: { type: String, required: false, default: '0' },
      height: { type: Number, required: false, default: 480 },
      isHighlighted: { type: Function, required: false, default: (node) => { return false; } },
      labelMaxLength: { type: Number, required: false, default: 16 },
      links: { type: Array, required: true },
      nodes: { type: Array, required: true },
      triggerEdgeUpdates: { type: Boolean, required: false, default: false },
      triggerFitView: { type: Boolean, required: false, default: false },
      triggerNodeUpdates: { type: Boolean, required: false, default: false },
      width: { type: Number, required: false, default: 640 },
    },
    data: function () {
      return {
        containerStyle: {
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          border: 'solid 1px transparent !important',
          borderRadius: '8px',
        },
        graphStyle: {
          autoCameraOrbitAngleStep: 0.003,
          bloomStrength: 2,
          bloomRadius: 1,
          bloomThreshold: 0.1,
          cooldownTicks: 10,
          cursorOnHover: 'pointer',
          distanceMax: 250.0,
          enableBloom: true,
          fitDuration: 1200,
          fitPadding: -3000,
          fontFamily: FontStyle.family.primary,
          hoverWithNeighbors: true,
          initCooldownTicks: 10,
          initWarmupTicks: 30,
          labelMargin: 2,
          linkWidth: 0.5,
          linkHoverPrecision: 40,
          nodeId: 'id',
          nodeRadius: 5,
          warmupTicks: 0,
        },
      };
    },
    created: function () {
      this.containerStyle = {
        ...this.containerStyle,
        ...this.$attrs,
      };

      this.graphStyle = {
        ...this.graphStyle,
        fontColor: {
          'default': this.color.dark2(),
          hover: this.color.action1(),
          hoverNeighbor: this.color.action1(),
          highlight: this.color.overlayBorder1(),
        },
        fontSize: {
          'default': FontStyle.size.sm,
          hover: null,
          hoverNeighbor: null,
          highlight: null,
        },
        fontWeight: {
          'default': FontStyle.weight.md,
          hover: FontStyle.weight.xl,
          hoverNeighbor: FontStyle.weight.lg,
          highlight: null,
        },
        nodeColor: {
          'default': this.color.dark2(),
          hover: this.color.dark4(),
          hoverNeighbor: this.color.dark4(),
          highlight: this.color.overlayBorder1(),
        },
        ringColor: {
          'default': null,
          hover: this.color.action1(),
          hoverNeighbor: this.color.action1(0.75),
          highlight: null,
        },
        ringWidth: {
          'default': null,
          hover: 1,
          hoverNeighbor: 1,
          highlight: null,
        },
      };
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      popover: function (node) {
        const commonStyle = `border-radius: ${Layout.space.sm};
                             box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                             font-family: ${FontStyle.family.primary};
                             font-size: ${FontStyle.size.sm};
                             font-weight: ${FontStyle.weight.md};
                             margin: -${Layout.space.sm};
                             min-width: 120px;
                             padding: ${Layout.space.sm} ${Layout.space.md};`;
        const styles = {
          'default': `background-color: ${this.color.light1()};
                      color: ${this.color.dark2()};` + commonStyle,
          'highlight': `background-color: ${this.color.overlayBorder1()};
                        color: ${this.color.light1()};` + commonStyle,
        };
        const nameStyle = `font-weight: ${FontStyle.weight.xl}`;
        const labelStyle = `color: ${this.color.dark3()}`;

        return node => {
          const highlight = !this.disableConditionalPopover && this.isHighlighted(node);
          const style = highlight ? styles.highlight : styles.default;

          const htmlPrefix = `
            <div style="${style}">
              <div style="${nameStyle}">
                ${node.name}
              </div>`;
          const htmlPostfix = `</div>`;

          const html = `
              ${htmlPrefix}
              <div>
                <span style="${labelStyle}"># of Experts:</span> ${node.userSize}
              </div>
            ${htmlPostfix}`;
          return html;
        };
      },
    },
  };
</script>
